import { Injectable, NgZone, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { API } from './constants';

@Injectable({
	providedIn: 'any',
})
export class SseService {
	private apiService = inject(ApiService);

	constructor(private ngZone: NgZone) {}

	connect(url: string): Observable<any> {
		return new Observable((observer) => {
			let eventSource: EventSource | null = null;
			let closed = false;

			// El EventSource del navegador no admite cabeceras, así que en vez de
			// mandar el JWT en la URL (?token=, expuesto en logs/analítica) pedimos
			// primero un ticket efímero de un solo uso (autenticado por header) y
			// abrimos el stream con ?ticket=. Ver hallazgo "JWT en URL".
			(async () => {
				try {
					const res = await this.apiService.post<{ ticket: string }>(API.sseTicket, { body: {} });
					const ticket = res?.data?.ticket;
					if (closed) return;
					if (!ticket) {
						this.ngZone.run(() => observer.error(new Error('No se pudo obtener el ticket SSE')));
						return;
					}

					const sseUrl = new URL(url);
					sseUrl.searchParams.append('ticket', ticket);
					// Bypass del service worker de Angular.
					sseUrl.searchParams.append('ngsw-bypass', 'true');

					eventSource = new EventSource(sseUrl.toString());

					eventSource.onmessage = (event) => {
						this.ngZone.run(() => {
							// Ignora eventos no-JSON (p.ej. keep-alives) sin romper el stream.
							let parsed: any;
							try {
								parsed = JSON.parse(event.data);
							} catch {
								return;
							}
							observer.next(parsed);
						});
					};

					eventSource.onerror = (error) => {
						this.ngZone.run(() => {
							if (!environment.production) {
								console.error('SSE connection error:', error);
							}
							observer.error(error);
						});
						eventSource?.close();
					};

					eventSource.onopen = () => {
						if (!environment.production) {
							console.log('SSE connection established');
						}
					};
				} catch (err) {
					if (!closed) {
						this.ngZone.run(() => observer.error(err));
					}
				}
			})();

			// Teardown: cubre también el caso de desuscribirse antes de que el ticket
			// resuelva y se abra el EventSource. La reconexión la maneja el consumidor
			// (se resuscribe → connect() pide un ticket nuevo).
			return () => {
				closed = true;
				eventSource?.close();
			};
		});
	}

	// Stream PÚBLICO (sin ticket): para pagadores anónimos (p.ej. el checkout de
	// pago de un paylink con crypto). El endpoint no exige sesión, así que se abre
	// el EventSource directo. La reconexión la maneja el consumidor (se resuscribe).
	connectPublic(url: string): Observable<any> {
		return new Observable((observer) => {
			const sseUrl = new URL(url);
			sseUrl.searchParams.append('ngsw-bypass', 'true');
			const eventSource = new EventSource(sseUrl.toString());

			eventSource.onmessage = (event) => {
				this.ngZone.run(() => {
					let parsed: any;
					try {
						parsed = JSON.parse(event.data);
					} catch {
						return;
					}
					observer.next(parsed);
				});
			};

			eventSource.onerror = (error) => {
				this.ngZone.run(() => {
					if (!environment.production) console.error('SSE (public) error:', error);
					observer.error(error);
				});
				eventSource.close();
			};

			return () => eventSource.close();
		});
	}
}
